"use client";

import { BigLoading } from "@/components/shared/loading";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { usePathname, useRouter } from "next/navigation";
import { createContext, useContext, useEffect, useState } from "react";

// ---- Types ----
type UserState = {
  user: App.User | null;
  loading: boolean;
  isAuthenticated: boolean;

  refreshUser: () => Promise<void>;
  logout: () => void;
  isLogout: boolean;
  isAdmin: boolean;
};

const UserContext = createContext<UserState | undefined>(undefined);

const adminEmail = process.env.NEXT_PUBLIC_IS_ADMIN_EMAIL;

export default function UserProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [user, setUser] = useState<App.User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isLogout, setIsLogout] = useState(false);

  const pathname = usePathname();
  const router = useRouter();

  // ---- Fetch the real user ----
  const refreshUser = async () => {
    try {
      const res = await fetch("/api/me", {
        method: "GET",
        credentials: "include", // Allow cookies to be sent
        cache: "no-store",
      });

      const data = await res.json();

      if (data.success) {
        setUser(data.user);
      } else {
        setUser(null);
      }
    } catch (err) {
      console.error("User refresh error:", err);
      setUser(null);
    }
  };

  // ---- Logout ----
  const logout = async () => {
    setIsLogout(true);

    try {
      await fetch("/api/logout", { method: "POST" });

      setUser(null);
      router.push("/auth/login");
    } finally {
      setIsLogout(false);
    }
  };

  // ---- Run on page change ----
  useEffect(() => {
    (async () => {
      await refreshUser();
      setLoading(false);
    })();
  }, [pathname]);

  const value: UserState = {
    user,
    loading,
    isAuthenticated: !!user,
    refreshUser,
    logout,
    isLogout,
    isAdmin: user?.email === adminEmail,
  };

  if (loading) return <BigLoading />;

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser() {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error("useUser must be used inside <UserProvider>");
  return ctx;
}

// Reusable Logout Modal Component
export function LogOutModal({
  open,
  onOpenChange,
  onLogout,
  title = "Logout",
  description = "Are you sure you want to logout? You'll need to sign in again to access your account.",
  cancelText = "Cancel",
  confirmText = "Logout",
}: {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onLogout?: () => void;
  title?: string;
  description?: string;
  cancelText?: string;
  confirmText?: string;
}) {
  const { isLogout } = useUser();

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="sm:max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-lg font-semibold">
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-gray-600">
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-0">
          <AlertDialogCancel asChild>
            <Button variant="outline" className="w-full sm:w-auto">
              {cancelText}
            </Button>
          </AlertDialogCancel>
          <div>
            <Button
              variant="destructive"
              onClick={onLogout}
              disabled={isLogout}
              className="w-full sm:w-auto bg-red-700 hover:bg-red-700 text-white"
            >
              {isLogout ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Logging out...
                </>
              ) : (
                confirmText
              )}
            </Button>
          </div>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
